<template>
  <div class="ap-document-detail">
    <div class="flex items-center justify-between mb-4">
      <router-link
        to="/ap-documents"
        class="flex items-center text-primary hover:underline"
      >
        <feather-icon icon="ArrowLeftIcon" svgClasses="w-4 h-4 mr-1" />
        Back to Supplier e-Invoices
      </router-link>
      <a
        v-if="doc && doc.publicUrl"
        :href="doc.publicUrl"
        target="_blank"
        rel="noopener noreferrer"
        class="flex items-center text-primary hover:underline"
      >
        View on MyInvois
        <feather-icon icon="ExternalLinkIcon" svgClasses="w-4 h-4 ml-1" />
      </a>
    </div>

    <div v-if="loading" class="flex justify-center items-center py-10">
      <vs-spinner color="primary" />
    </div>

    <div v-else-if="doc" class="ap-document-body">
      <!-- Header -->
      <vx-card class="mb-5">
        <div class="flex items-center justify-between flex-wrap">
          <div>
            <h2 class="font-semibold">
              {{ typeLabel(doc.typeName) }} {{ doc.internalId || '—' }}
            </h2>
            <p class="text-sm text-gray-500">
              Received {{ doc.dateTimeReceived ? formatDate(doc.dateTimeReceived) : '—' }}
              <span v-if="doc.dateTimeValidated">
                · Validated {{ formatDate(doc.dateTimeValidated) }}</span
              >
            </p>
          </div>
          <div class="text-right">
            <vs-chip :color="statusColor(doc.status)">{{ doc.status }}</vs-chip>
          </div>
        </div>
        <div
          v-if="doc.status !== 'Valid' && doc.documentStatusReason"
          class="mt-3"
        >
          <p class="text-xs text-gray-500">Status reason</p>
          <p class="text-sm">{{ doc.documentStatusReason }}</p>
        </div>
      </vx-card>

      <div class="vx-row">
        <!-- Parties -->
        <div class="vx-col md:w-1/2 w-full mb-5">
          <vx-card title="Supplier" no-shadow card-border>
            <dl class="detail-list">
              <dt>Name</dt><dd>{{ doc.supplierName || '—' }}</dd>
              <dt>TIN</dt><dd>{{ doc.supplierTIN || '—' }}</dd>
              <dt>Reg / ID</dt><dd>{{ doc.issuerID || '—' }}</dd>
              <dt>ID Type</dt><dd>{{ doc.issuerIDType || '—' }}</dd>
            </dl>
          </vx-card>
        </div>
        <div class="vx-col md:w-1/2 w-full mb-5">
          <vx-card title="Buyer (you)" no-shadow card-border>
            <dl class="detail-list">
              <dt>Name</dt><dd>{{ doc.buyerName || '—' }}</dd>
              <dt>TIN</dt><dd>{{ doc.buyerTIN || '—' }}</dd>
              <dt>Reg / ID</dt><dd>{{ doc.receiverID || '—' }}</dd>
              <dt>Issue Date</dt>
              <dd>{{ doc.dateTimeIssued ? formatDate(doc.dateTimeIssued) : '—' }}</dd>
            </dl>
          </vx-card>
        </div>
      </div>

      <!-- Monetary -->
      <vx-card title="Amounts" no-shadow card-border class="mb-5">
        <div class="vx-row">
          <div class="vx-col md:w-1/4 w-full">
            <p class="text-xs text-gray-500">Net (excl. tax)</p>
            <p class="font-semibold">{{ formatAmount(doc.totalExcludingTax) }}</p>
          </div>
          <div class="vx-col md:w-1/4 w-full">
            <p class="text-xs text-gray-500">Discount</p>
            <p class="font-semibold">{{ formatAmount(doc.totalDiscount) }}</p>
          </div>
          <div class="vx-col md:w-1/4 w-full">
            <p class="text-xs text-gray-500">Net amount</p>
            <p class="font-semibold">{{ formatAmount(doc.totalNetAmount) }}</p>
          </div>
          <div class="vx-col md:w-1/4 w-full">
            <p class="text-xs text-gray-500">Total payable</p>
            <p class="font-semibold text-primary">
              {{ formatAmount(doc.totalPayableAmount) }}
            </p>
          </div>
        </div>
      </vx-card>

      <!-- Line items -->
      <vx-card title="Line items" no-shadow card-border class="mb-5">
        <vs-table v-if="lineItems.length" :data="lineItems" stripe>
          <template slot="thead">
            <vs-th>#</vs-th>
            <vs-th>Description</vs-th>
            <vs-th>Class.</vs-th>
            <vs-th>Qty</vs-th>
            <vs-th>Unit</vs-th>
            <vs-th>Unit Price</vs-th>
            <vs-th>Discount</vs-th>
            <vs-th>Taxable</vs-th>
            <vs-th>Tax</vs-th>
            <vs-th>Line Total</vs-th>
          </template>
          <template slot-scope="{ data }">
            <vs-tr :key="i" v-for="(line, i) in data">
              <vs-td>{{ line.lineNumber != null ? line.lineNumber : i + 1 }}</vs-td>
              <vs-td>
                <p class="text-sm font-medium">{{ line.description || '—' }}</p>
              </vs-td>
              <vs-td>
                <span v-if="line.classificationCode">
                  {{ line.classificationCode }}
                  <span v-if="line.classificationType" class="text-xs text-gray-500"
                    >({{ line.classificationType }})</span
                  >
                </span>
                <span v-else>—</span>
              </vs-td>
              <vs-td>{{ line.quantity != null ? formatNumber(line.quantity) : '—' }}</vs-td>
              <vs-td>{{ line.unitCode || '—' }}</vs-td>
              <vs-td>{{ line.unitPrice != null ? formatAmount(line.unitPrice) : '—' }}</vs-td>
              <vs-td>{{ line.discountAmount ? formatAmount(line.discountAmount) : '—' }}</vs-td>
              <vs-td>{{ line.taxableAmount != null ? formatAmount(line.taxableAmount) : '—' }}</vs-td>
              <vs-td>
                <p>{{ line.taxAmount != null ? formatAmount(line.taxAmount) : '—' }}</p>
                <p class="text-xs text-gray-500">{{ taxLabel(line) }}</p>
              </vs-td>
              <vs-td>{{ line.totalAmount != null ? formatAmount(line.totalAmount) : '—' }}</vs-td>
            </vs-tr>
          </template>
        </vs-table>

        <!-- payloadParsed === false: header synced but lines not yet retrieved -->
        <div v-else class="py-6 text-center">
          <feather-icon
            icon="FileTextIcon"
            svgClasses="w-10 h-10 text-gray-300 mb-2"
          />
          <p class="text-sm text-gray-500">
            <template v-if="doc.payloadParsed === false">
              Line details have not been retrieved for this document yet. They
              become available once the full document payload is fetched from
              MyInvois.
            </template>
            <template v-else>No line items were parsed for this document.</template>
          </p>
        </div>
      </vx-card>

      <!-- Tax summary -->
      <vx-card
        v-if="taxSummary.length"
        title="Tax summary"
        no-shadow
        card-border
        class="mb-5"
      >
        <vs-table :data="taxSummary" stripe>
          <template slot="thead">
            <vs-th>Tax type</vs-th>
            <vs-th>Rate</vs-th>
            <vs-th>Taxable</vs-th>
            <vs-th>Tax amount</vs-th>
          </template>
          <template slot-scope="{ data }">
            <vs-tr :key="row.key" v-for="row in data">
              <vs-td>{{ row.typeLabel }}</vs-td>
              <vs-td>{{ row.rate != null ? row.rate + '%' : '—' }}</vs-td>
              <vs-td>{{ formatAmount(row.taxable) }}</vs-td>
              <vs-td>{{ formatAmount(row.tax) }}</vs-td>
            </vs-tr>
          </template>
        </vs-table>
      </vx-card>

      <!-- Document meta -->
      <vx-card title="Document" no-shadow card-border>
        <dl class="detail-list">
          <dt>Document Type</dt><dd>{{ typeLabel(doc.typeName) }}</dd>
          <dt>Supplier Doc No.</dt><dd>{{ doc.internalId || '—' }}</dd>
          <dt>Status</dt><dd>{{ doc.status }}</dd>
          <dt>Currency</dt><dd>{{ doc.documentCurrency || '—' }}</dd>
          <dt>LHDN UUID</dt><dd class="break-all">{{ doc.uuid || '—' }}</dd>
          <dt>Submission UID</dt><dd class="break-all">{{ doc.submissionUid || '—' }}</dd>
          <dt>Long ID</dt><dd class="break-all">{{ doc.longId || '—' }}</dd>
          <dt>Issued</dt>
          <dd>{{ doc.dateTimeIssued ? formatDateTime(doc.dateTimeIssued) : '—' }}</dd>
          <dt>Validated</dt>
          <dd>{{ doc.dateTimeValidated ? formatDateTime(doc.dateTimeValidated) : '—' }}</dd>
        </dl>
      </vx-card>
    </div>

    <div v-else class="text-center py-10">
      <p class="text-gray-500">Document not found.</p>
    </div>
  </div>
</template>

<script>
import moment from 'moment'

// LHDN tax type code list (MyInvois). Fallback to raw code when unmapped.
const TAX_TYPE_LABELS = {
  '01': 'Sales Tax',
  '02': 'Service Tax',
  '03': 'Tourism Tax',
  '04': 'High-Value Goods Tax',
  '05': 'Sales Tax (low-value goods)',
  '06': 'Not Applicable',
  E: 'Tax exemption',
}

export default {
  data() {
    return {
      loading: false,
      doc: null,
    }
  },
  computed: {
    lineItems() {
      return (this.doc && Array.isArray(this.doc.lines) && this.doc.lines) || []
    },
    // Group lines by (taxType, taxRate) for a tax breakdown footer — the thing
    // AP reconciliation cares about more than the AR side does.
    taxSummary() {
      const groups = {}
      this.lineItems.forEach((line) => {
        if (line.taxAmount == null && line.taxableAmount == null) return
        const rate = line.taxRate != null ? Number(line.taxRate) : null
        const key = `${line.taxType || '—'}|${rate}`
        if (!groups[key]) {
          groups[key] = {
            key,
            typeLabel: this.taxTypeLabel(line.taxType),
            rate,
            taxable: 0,
            tax: 0,
          }
        }
        groups[key].taxable += Number(line.taxableAmount) || 0
        groups[key].tax += Number(line.taxAmount) || 0
      })
      return Object.values(groups)
    },
  },
  created() {
    this.fetch()
  },
  methods: {
    async fetch() {
      this.loading = true
      try {
        this.doc = await this.$store.dispatch(
          'apDocuments/fetchApDocument',
          this.$route.params.uuid
        )
      } catch (e) {
        console.error('[ApDocuments] detail fetch failed:', e)
        this.$vs.notify({
          color: 'danger',
          title: 'Error',
          text:
            (e.response && e.response.data && e.response.data.message) ||
            'Failed to load AP document',
        })
      } finally {
        this.loading = false
      }
    },
    typeLabel(type) {
      return (
        {
          Invoice: 'Invoice',
          CreditNote: 'Credit Note',
          DebitNote: 'Debit Note',
          RefundNote: 'Refund Note',
        }[type] || type || 'Document'
      )
    },
    taxTypeLabel(code) {
      if (!code) return '—'
      return TAX_TYPE_LABELS[code] || code
    },
    taxLabel(line) {
      if (line.taxType == null && line.taxRate == null) return ''
      const type = this.taxTypeLabel(line.taxType)
      const rate = line.taxRate != null ? `${line.taxRate}%` : ''
      return [type, rate].filter(Boolean).join(' · ')
    },
    statusColor(status) {
      return (
        {
          Valid: 'success',
          Cancelled: 'warning',
          Rejected: 'danger',
        }[status] || 'primary'
      )
    },
    formatAmount(amount) {
      if (amount == null || isNaN(amount)) return '—'
      const value = Number(amount).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
      return this.doc && this.doc.documentCurrency
        ? `${this.doc.documentCurrency} ${value}`
        : value
    },
    formatNumber(value) {
      if (value == null || isNaN(value)) return '—'
      return Number(value).toLocaleString(undefined, {
        maximumFractionDigits: 4,
      })
    },
    formatDate(d) {
      return d ? moment(d).format('DD MMM YYYY') : '—'
    },
    formatDateTime(d) {
      return d ? moment(d).format('DD MMM YYYY, hh:mm A') : '—'
    },
  },
}
</script>

<style lang="scss" scoped>
.detail-list {
  display: grid;
  grid-template-columns: 140px 1fr;
  row-gap: 0.5rem;
  column-gap: 1rem;

  dt {
    color: #6b7280;
    font-size: 0.8rem;
  }
  dd {
    font-size: 0.9rem;
    margin: 0;
  }
}
</style>
